import type {
	FilesTableMetaVisibility,
	FilesTableSortState,
} from '../settings.model';
import {
	isFilesTableSortDirection,
	isFilesTableSortPreference,
	normalizeFilesTableMetaVisibility,
	persistFilesTablePreferences,
} from '../settings.utils';

/**
 * Files table preference actions.
 *
 * Persists sort and metadata visibility per table key and triggers debounced
 * preference sync to the backend.
 */
export const filesTablePreferencesActions = {
	/**
	 * Returns the stored files table sort state for a table key.
	 */
	getFilesTableSort(this: any, tableKey: string): FilesTableSortState | null {
		if (!tableKey) {
			return null;
		}

		const sort = this.filesTablePreferences[tableKey]?.sort;
		if (
			!sort ||
			!isFilesTableSortPreference(sort.key) ||
			!isFilesTableSortDirection(sort.direction)
		) {
			return null;
		}

		return {
			key: sort.key,
			direction: sort.direction,
		};
	},

	/**
	 * Stores files table sort state for a table key.
	 */
	setFilesTableSort(
		this: any,
		tableKey: string,
		sort: FilesTableSortState
	): void {
		if (
			!tableKey ||
			!sort ||
			!isFilesTableSortPreference(sort.key) ||
			!isFilesTableSortDirection(sort.direction)
		) {
			return;
		}

		const currentSort = this.filesTablePreferences[tableKey]?.sort;
		if (
			currentSort?.key === sort.key &&
			currentSort?.direction === sort.direction
		) {
			return;
		}

		const currentEntry = this.filesTablePreferences[tableKey] || {};

		this.filesTablePreferences = {
			...this.filesTablePreferences,
			[tableKey]: {
				...currentEntry,
				sort: {
					key: sort.key,
					direction: sort.direction,
				},
			},
		};
		persistFilesTablePreferences(this.filesTablePreferences);
		this.queueUIPreferencesUpsert();
	},

	/**
	 * Returns the stored metadata visibility state for a table key.
	 */
	getFilesTableMetaVisibility(
		this: any,
		tableKey: string
	): FilesTableMetaVisibility[] | null {
		if (!tableKey) {
			return null;
		}

		const visibility = this.filesTablePreferences[tableKey]?.metaVisibility;
		if (!Array.isArray(visibility)) {
			return null;
		}

		return visibility.map((entry) => ({
			key: entry.key,
			visible: Boolean(entry.visible),
		}));
	},

	/**
	 * Stores metadata visibility for a table key.
	 */
	setFilesTableMetaVisibility(
		this: any,
		tableKey: string,
		visibility: FilesTableMetaVisibility[]
	): void {
		if (!tableKey) {
			return;
		}

		const normalizedVisibility = normalizeFilesTableMetaVisibility(visibility);
		if (!normalizedVisibility.length) {
			return;
		}

		const currentEntry = this.filesTablePreferences[tableKey] || {};
		const currentVisibility = currentEntry.metaVisibility || [];
		const isSame =
			currentVisibility.length === normalizedVisibility.length &&
			currentVisibility.every(
				(entry, index) =>
					entry.key === normalizedVisibility[index]?.key &&
					Boolean(entry.visible) ===
						Boolean(normalizedVisibility[index]?.visible)
			);

		if (isSame) {
			return;
		}

		this.filesTablePreferences = {
			...this.filesTablePreferences,
			[tableKey]: {
				...currentEntry,
				metaVisibility: normalizedVisibility,
			},
		};
		persistFilesTablePreferences(this.filesTablePreferences);
		this.queueUIPreferencesUpsert();
	},
};
